import { TranslateService } from '@ngx-translate/core';

/**
 * Format date value to Vietnamese locale string (HH:mm DD/MM/YYYY)
 * @param value - Date input (Date object or string)
 * @returns Formatted date string or '-'
 */
export function formatDeliveryDate(value: any): string {
  if (!value) {
    return '-';
  }

  if (value instanceof Date) {
    return value.toLocaleString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  if (typeof value === 'string') {
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      return date.toLocaleString('vi-VN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  }

  return value.toString();
}

/**
 * Map numeric status to string status
 * Status: 0 CREATED, 1 AWAIT_STOCK_OUT, 2 FAILED_TO_NOTIFY_WMS, 3 SHIPPED, 4 PARTIALLY_SHIPPED, 5 CANCELLED, 6 PICKED
 * @param status - Status number or string
 * @returns Status string
 */
export function mapStatusToString(status?: number | string): string {
  // If status is already a string, return it
  if (typeof status === 'string') {
    return status;
  }
  
  // Map numeric status to string status
  const statusMap: { [key: number]: string } = {
    0: 'PENDING', // Waiting to notify WMS
    1: 'APPROVED', // Waiting to ship -> APPROVED + PICKING
    2: 'REJECT', // Failed to notify WMS
    3: 'SHIPPED', // Fully shipped
    4: 'PARTIALLY_SHIPPED', // Partially shipped
    5: 'CANCELLED', // Canceled
    6: 'PICKED', // All items picked, awaiting stock-out
  };
  
  if (status !== undefined && status !== null && statusMap[status] !== undefined) {
    return statusMap[status];
  }
  
  return status?.toString() || '';
}

/**
 * Get status severity color for PrimeNG Tag component
 * @param status - Status string or number
 * @returns Severity string: 'info' | 'success' | 'warning' | 'danger'
 */
export function getDeliveryStatusSeverity(status: string | number): string {
  // Convert to string if it's a number
  const statusStr = mapStatusToString(status);
  
  const statusMap: { [key: string]: string } = {
    'PENDING': 'info',
    'APPROVED': 'success',
    'PICKED': 'success',
    'SHIPPED': 'success',
    'PARTIALLY_SHIPPED': 'warning',
    'REJECT': 'danger',
    'REJECTED': 'danger',
    'CANCELLED': 'warning',
  };
  return statusMap[statusStr] || 'info';
}

/**
 * Get translated status label
 * @param status - Status string or number
 * @param translateService - TranslateService instance
 * @param includePartiallyShipped - Whether to include PARTIALLY_SHIPPED status mapping
 * @param outType - Outbound type (order-sales-out-stock for SO, transfer-out-stock for ODO)
 * @returns Translated status label
 */
export function getDeliveryStatusLabel(
  status: string | number,
  translateService: TranslateService,
  includePartiallyShipped: boolean = false,
  outType?: string
): string {
  if (status === undefined || status === null || status === '') {
    return '-';
  }

  // Convert to string if it's a number
  const statusStr = mapStatusToString(status);

  const statusMap: { [key: string]: string } = {
    'PENDING': 'outbound_management.status.pending',
    'CREATED': 'outbound_management.status.pending', // Legacy support
    'APPROVED': 'outbound_management.status.approved',
    'PICKING': 'outbound_management.status.approved',
    'READY_TO_SHIP': 'outbound_management.status.approved',
    'PICKED': 'outbound_management.status.picked',
    'SHIPPED': 'outbound_management.status.shipped',
    'REJECT': 'outbound_management.status.reject',
    'REJECTED': 'outbound_management.status.reject',
    'CANCELED': 'outbound_management.status.cancelled',
    'CANCELLED': 'outbound_management.status.cancelled',
  };

  // For SHIPPED status, check outType to determine label
  if (statusStr === 'SHIPPED') {
    // If outType is sales order (order-sales-out-stock), use "Đã giao hàng"
    if (outType === 'order-sales-out-stock') {
      return translateService.instant('outbound_management.status.shipped_for_sales_order');
    }
    // Otherwise (transfer order), use "Đã xuất hàng"
    return translateService.instant('outbound_management.status.shipped');
  }

  if (includePartiallyShipped) {
    statusMap['PARTIALLY_SHIPPED'] = 'outbound_management.status.partially_shipped';
  }

  const translationKey = statusMap[statusStr];
  if (translationKey) {
    return translateService.instant(translationKey);
  }

  return statusStr;
}

/**
 * Get outbound type label (translated)
 * @param outTypeMeaning - Outbound type meaning from API
 * @param translateService - TranslateService instance
 * @returns Translated outbound type label
 */
export function getOutboundTypeLabel(
  outTypeMeaning: string,
  translateService: TranslateService
): string {
  if (!outTypeMeaning) {
    return '-';
  }

  // If it's already a translation key, translate it
  if (outTypeMeaning.startsWith('outbound_management.')) {
    return translateService.instant(outTypeMeaning);
  }

  // Map raw API values to translation keys
  const outTypeMap: { [key: string]: string } = {
    'order-sales-out-stock': 'outbound_management.outbound_type.sales_order',
    'transfer-order-out-stock': 'outbound_management.outbound_type.transfer_order',
    'transfer-out-stock': 'outbound_management.outbound_type.transfer_order',
    'purchase-order-return-out-stock': 'outbound_management.outbound_type.purchase_return_order',
    'purchase-return-order': 'outbound_management.outbound_type.purchase_return_order',
  };

  const translationKey = outTypeMap[outTypeMeaning];
  if (translationKey) {
    return translateService.instant(translationKey);
  }

  // Fallback to original value if no mapping found
  return outTypeMeaning;
}

/**
 * Format number to Vietnamese locale string
 * @param value - Number value
 * @returns Formatted number string or '-'
 */
export function formatDeliveryNumber(value: number | undefined): string {
  if (value === undefined || value === null) {
    return '-';
  }
  return value.toLocaleString('vi-VN');
}

/**
 * Get warehouse or store name based on outType
 * transfer-out-stock: outLogicWarehouseName (Tên kho xuất)
 * order-sales-out-stock: shopName (Cửa hàng bán)
 * @param record - Record with outType, outLogicWarehouseName, and shopName
 * @returns Warehouse name or store name or '-'
 */
export function getWarehouseOrStoreName(record: {
  outType?: string;
  outLogicWarehouseName?: string;
  shopName?: string;
}): string {
  if (!record) {
    return '-';
  }
  
  // If it's a transfer order, use outLogicWarehouseName
  if (record.outType === 'transfer-out-stock') {
    return record.outLogicWarehouseName || '-';
  }
  
  // If it's a sales order, use shopName
  if (record.outType === 'order-sales-out-stock') {
    return record.shopName || '-';
  }
  
  // Fallback to outLogicWarehouseName if outType is not recognized
  return record.outLogicWarehouseName || record.shopName || '-';
}

